// Long free text that must be readable without owning the page: shows the first few lines and a
// "Show more". Nothing is hidden from the record — the full text is one click away and the audit
// trail carries it regardless — but a 2,000-character reason for change must not push the work
// a reviewer came to do below the fold.
import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface ClampedTextProps {
  children: string;
  /** How many lines to show collapsed. */
  lines?: number;
  className?: string;
}

export function ClampedText({ children, lines = 4, className }: ClampedTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [clampable, setClampable] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  // Only offer "Show more" when the text actually overflows: a two-line reason with a toggle
  // under it looks broken.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setClampable(el.scrollHeight - el.clientHeight > 2);
  }, [children, lines]);

  return (
    <>
      <p
        ref={ref}
        className={cn('whitespace-pre-wrap [overflow-wrap:anywhere]', className)}
        style={expanded ? undefined : { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lines, overflow: 'hidden' }}
      >
        {children}
      </p>
      {(clampable || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-1 text-[12px] font-medium text-muted hover:text-navy"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  );
}
